// „Gelesen"-Stand für „Was ist neu?" je Person (User 01.10.2026: frühere
// Release-Logs sollen bleiben, bis man sie gelesen hat).
//
// Ablage wie die Mail-Ansicht (mail/useMailAnsicht.ts): schneller Startwert aus
// dem Browser, maßgeblich die `UserPreference`-Ablage am Server — so gilt
// „gelesen" auch am anderen Rechner. Speichern ist best effort.
//
// Gemerkt wird eine Menge von Commit-Kennungen statt eines Zeitpunkts: auf PROD
// kommen dev-Commits erst mit der Promotion an und tragen dann ältere Daten als
// das, was man zuletzt gelesen hat. `seit` ist nur die Untergrenze vom ersten
// Öffnen (alles davor gilt als gesehen).

import { fetchUserPreferences, getCurrentUserPreferenceKey, saveUserPreference } from "../../utils/userPreferences";
import type { GesehenStand } from "./aenderungen";

export const WAS_IST_NEU_SCOPE = "app.wasIstNeu.gesehen";
export const WAS_IST_NEU_LOCAL_KEY = "kmvs.wasIstNeu.gesehen";
/** Obergrenze der gemerkten Kennungen — die Änderungsliste hat höchstens 500. */
export const MAX_GESEHEN = 800;

const DATUM = /^\d{4}-\d{2}-\d{2}$/;

export function normalisiereGesehen(wert: unknown): GesehenStand | null {
  const roh = typeof wert === "string" ? (() => { try { return JSON.parse(wert); } catch { return null; } })() : wert;
  if (!roh || typeof roh !== "object") return null;
  const { seit, shas } = roh as { seit?: unknown; shas?: unknown };
  if (typeof seit !== "string" || !DATUM.test(seit)) return null;
  const liste = Array.isArray(shas) ? shas.filter((s): s is string => typeof s === "string" && !!s) : [];
  return { seit, shas: liste.slice(-MAX_GESEHEN) };
}

/** Zwei Stände zusammenführen (Browser + Server): die frühere Untergrenze, alle Kennungen. */
export function vereinige(a: GesehenStand | null, b: GesehenStand | null): GesehenStand | null {
  if (!a) return b;
  if (!b) return a;
  const shas = Array.from(new Set([...a.shas, ...b.shas])).slice(-MAX_GESEHEN);
  return { seit: a.seit < b.seit ? a.seit : b.seit, shas };
}

export function leseLokal(): GesehenStand | null {
  try {
    return normalisiereGesehen(window.localStorage.getItem(WAS_IST_NEU_LOCAL_KEY));
  } catch {
    return null;
  }
}

function schreibeLokal(stand: GesehenStand): void {
  try {
    window.localStorage.setItem(WAS_IST_NEU_LOCAL_KEY, JSON.stringify(stand));
  } catch {
    /* privates Fenster / blockierter Speicher — der Serverwert trägt */
  }
}

/** Stand beim Öffnen des Popups: Browser-Wert, ergänzt um den des Servers. */
export async function ladeGesehen(): Promise<GesehenStand | null> {
  const lokal = leseLokal();
  try {
    const werte = await fetchUserPreferences([WAS_IST_NEU_SCOPE]);
    const zusammen = vereinige(lokal, normalisiereGesehen(werte[WAS_IST_NEU_SCOPE]));
    if (zusammen) schreibeLokal(zusammen);
    return zusammen;
  } catch {
    return lokal;
  }
}

/**
 * Merkt `shas` als gelesen. `seit` gilt nur, wenn es noch keinen Stand gibt
 * (erstes Öffnen: Beginn des gezeigten Zeitraums). `aktuell` (die Kennungen der
 * ausgelieferten Liste) räumt Kennungen ab, die dort nicht mehr vorkommen.
 */
export function merkeGesehen(
  vorher: GesehenStand | null,
  shas: Iterable<string>,
  seit: string,
  aktuell?: Set<string>,
): GesehenStand {
  const basis = vereinige(vorher, leseLokal());
  const alle = Array.from(new Set([...(basis?.shas ?? []), ...Array.from(shas)]));
  const behalten = aktuell && aktuell.size ? alle.filter((s) => aktuell.has(s)) : alle;
  const stand: GesehenStand = { seit: basis?.seit ?? seit, shas: behalten.slice(-MAX_GESEHEN) };
  schreibeLokal(stand);
  void (async () => {
    try {
      const userKey = await getCurrentUserPreferenceKey();
      if (!userKey) return;
      await saveUserPreference(userKey, WAS_IST_NEU_SCOPE, stand);
    } catch {
      /* best effort — im Browser ist es gemerkt */
    }
  })();
  return stand;
}

/** „Was ist neu?" von überall öffnen (Benutzermenü), auch ohne neue Version. */
export const WAS_IST_NEU_EREIGNIS = "kmvs:was-ist-neu";

export function oeffneWasIstNeu(): void {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(WAS_IST_NEU_EREIGNIS));
}
